/**
 * Expense Detail Modal
 *
 * Shows detailed information about an expense when clicked.
 * Includes an in-app receipt viewer for uploaded receipts.
 */

import { useState, useEffect } from 'react';
import { Modal, View, ScrollView, Pressable, Image, ActivityIndicator, Linking, Platform, Alert } from 'react-native';
import * as Sharing from 'expo-sharing';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { effectiveTopInset } from '@/utils/effectiveTopInset';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import type { Expense } from '@/services/expenses';
import { formatCurrency } from '@/utils/currency';
import { coerceDate } from '@/utils/dateKeys';
import { DualCurrencyDisplay } from './DualCurrencyDisplay';
import { calculateSplitAmounts, getUserShare, getUserPaid } from '@/utils/expenseSplitting';
import { WebView } from 'react-native-webview';
import { DocumentAttachmentPdfModal } from '@/components/documents/DocumentAttachmentPdfModal';
import { downloadUrlToTempFile } from '@/utils/downloadUrlToTempFile';
import { networkMonitor } from '@/services/networkMonitor';
import { getDocumentWebViewOriginWhitelist, isDocumentPreviewWebViewUrl } from '@/utils/documentWebViewAllowlist';

type ExpenseDetailModalProps = {
  visible: boolean;
  expense: Expense | null;
  onClose: () => void;
  onEdit?: (expense: Expense) => void;
  onDelete?: (expense: Expense) => void;
  canEdit?: boolean;
  baseCurrency?: string;
  tripMembers?: Array<{ userId: string; name?: string; email: string }>;
  currentUserId?: string;
};

const getCategoryIcon = (category: Expense['category']): keyof typeof Ionicons.glyphMap => {
  switch (category) {
    case 'flight':
      return 'airplane-outline';
    case 'lodging':
      return 'bed-outline';
    case 'food':
      return 'restaurant-outline';
    case 'transport':
      return 'car-outline';
    case 'activity':
      return 'ticket-outline';
    case 'shopping':
      return 'bag-outline';
    default:
      return 'receipt-outline';
  }
};

const getCategoryLabel = (category: Expense['category']): string => {
  switch (category) {
    case 'flight':
      return 'Flight';
    case 'lodging':
      return 'Lodging';
    case 'food':
      return 'Food & Dining';
    case 'transport':
      return 'Transportation';
    case 'activity':
      return 'Activity';
    case 'shopping':
      return 'Shopping';
    default:
      return 'Other';
  }
};

export function ExpenseDetailModal({
  visible,
  expense,
  onClose,
  onEdit,
  onDelete,
  canEdit = false,
  baseCurrency = 'USD',
  tripMembers = [],
  currentUserId,
}: ExpenseDetailModalProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const modalTopInset = effectiveTopInset(insets.top);
  const safeExpenseDate = expense ? coerceDate(expense.date) : null;
  const [showReceiptViewer, setShowReceiptViewer] = useState(false);
  const [nativeReceiptPdfUri, setNativeReceiptPdfUri] = useState<string | null>(null);
  const [nativeReceiptPdfError, setNativeReceiptPdfError] = useState(false);

  const offline = !networkMonitor.isOnline();
  const receiptRemote = expense?.receiptUrl ?? '';
  const receiptLocal = expense?.receiptLocalUri ?? '';
  const displayReceiptUrl = expense
    ? offline && receiptLocal
      ? receiptLocal
      : receiptRemote || receiptLocal || ''
    : '';
  const isReceiptPdf = /\.pdf(\?|$)/i.test(displayReceiptUrl);
  const showNativePdfReceipt =
    Platform.OS !== 'web' && isReceiptPdf && showReceiptViewer && Boolean(displayReceiptUrl);
  const receiptUnavailableOffline =
    Boolean(expense) &&
    offline &&
    /^https?:\/\//i.test(receiptRemote) &&
    !receiptLocal;

  useEffect(() => {
    if (!showNativePdfReceipt || !displayReceiptUrl) {
      setNativeReceiptPdfUri(null);
      setNativeReceiptPdfError(false);
      return;
    }
    let cancelled = false;
    setNativeReceiptPdfUri(null);
    setNativeReceiptPdfError(false);
    void downloadUrlToTempFile(displayReceiptUrl, 'receipt.pdf')
      .then((uri) => {
        if (!cancelled) setNativeReceiptPdfUri(uri);
      })
      .catch(() => {
        if (!cancelled) setNativeReceiptPdfError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [showNativePdfReceipt, displayReceiptUrl]);

  const closeReceiptViewer = () => {
    setShowReceiptViewer(false);
    setNativeReceiptPdfUri(null);
    setNativeReceiptPdfError(false);
  };

  if (!expense) return null;

  const categoryIcon = getCategoryIcon(expense.category);
  const categoryLabel = getCategoryLabel(expense.category);

  // Calculate split details if applicable
  const splitDetails = expense.split && expense.split.splitType !== 'none' 
    ? calculateSplitAmounts(expense, expense.split)
    : null;

  const getMemberName = (userId: string): string => {
    const member = tripMembers.find((m) => m.userId === userId);
    if (member) {
      return member.name || member.email.split('@')[0] || 'Unknown';
    }
    return userId === currentUserId ? 'You' : 'Unknown';
  };

  const sortedSplitEntries =
    splitDetails && expense.split
      ? Object.entries(splitDetails).sort((a, b) =>
          getMemberName(a[0]).localeCompare(getMemberName(b[0])),
        )
      : [];

  const splitParticipantCount = splitDetails ? Object.keys(splitDetails).length : 0;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
        {/* Header */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: theme.spacing(2),
            paddingTop: modalTopInset + theme.spacing(2),
            paddingBottom: theme.spacing(2),
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.border,
          }}
        >
          <ThemedText variant="heading">Expense Details</ThemedText>
          <Pressable
            onPress={onClose}
            style={({ pressed }) => ({
              padding: theme.spacing(1),
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <Ionicons name="close" size={24} color={theme.colors.text} />
          </Pressable>
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: theme.spacing(2), gap: theme.spacing(2) }}>
          {/* Main Info Card */}
          <Card>
            <View style={{ flexDirection: 'row', gap: theme.spacing(2), alignItems: 'flex-start' }}>
              <View
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: theme.radii.md,
                  backgroundColor: theme.colors.primary + '20',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ionicons name={categoryIcon} size={24} color={theme.colors.primary} />
              </View>
              <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                <ThemedText variant="heading" style={{ fontSize: 20 }}>
                  {expense.description}
                </ThemedText>
                <Caption color={theme.colors.textSecondary}>
                  {categoryLabel}
                </Caption>
                <View style={{ marginTop: theme.spacing(1) }}>
                  <DualCurrencyDisplay
                    amount={expense.amount}
                    currency={expense.currency || 'USD'}
                    baseCurrency={baseCurrency}
                    size="large"
                  />
                </View>
              </View>
            </View>
          </Card>

          {/* Details Card */}
          <Card>
            <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>Details</SubHeading>
            <View style={{ gap: theme.spacing(1.5) }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <Caption color={theme.colors.textSecondary}>Date</Caption>
                <Body>
                  {safeExpenseDate
                    ? safeExpenseDate.toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'Date TBD'}
                </Body>
              </View>
              
              {expense.merchant && (
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Caption color={theme.colors.textSecondary}>Merchant</Caption>
                  <Body>{expense.merchant}</Body>
                </View>
              )}

              {expense.location && (
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Caption color={theme.colors.textSecondary}>Location</Caption>
                  <Body>{expense.location}</Body>
                </View>
              )}

              {expense.paymentMethod && (
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Caption color={theme.colors.textSecondary}>Payment Method</Caption>
                  <Body style={{ textTransform: 'capitalize' }}>{expense.paymentMethod}</Body>
                </View>
              )}

              {(expense.receiptUrl || expense.receiptLocalUri) && (
                <View style={{ marginTop: theme.spacing(1) }}>
                  <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(0.5) }}>
                    Receipt
                  </Caption>
                  {receiptUnavailableOffline ? (
                    <Caption color={theme.colors.textSecondary}>
                      Open the Wallet tab online once so we can save a copy for offline viewing.
                    </Caption>
                  ) : (
                    <Pressable
                      onPress={() => {
                        setShowReceiptViewer(true);
                      }}
                      style={({ pressed }) => ({
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                        padding: theme.spacing(1),
                        borderRadius: theme.radii.md,
                        backgroundColor: theme.colors.backgroundSecondary,
                        opacity: pressed ? 0.7 : 1,
                      })}
                    >
                      <Ionicons name="receipt-outline" size={20} color={theme.colors.primary} />
                      <Body color={theme.colors.primary}>View Receipt</Body>
                    </Pressable>
                  )}
                </View>
              )}
            </View>
          </Card>

          {/* Split Details Card */}
          {expense.split && expense.split.splitType !== 'none' && splitDetails && (
            <Card>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: theme.spacing(1.5),
                }}
              >
                <SubHeading>Split Details</SubHeading>
                <Pressable
                  onPress={() =>
                    Alert.alert(
                      'About expense splits',
                      'Each row is someone’s share of the bill. Whoever paid covered the charge; the amounts show how it is divided. Your share is your portion of the total. Your net is what you paid minus your share (positive means others effectively owe you for this expense).',
                    )
                  }
                  hitSlop={10}
                  accessibilityRole="button"
                  accessibilityLabel="Explain split details"
                >
                  <Ionicons name="information-circle-outline" size={22} color={theme.colors.textSecondary} />
                </Pressable>
              </View>
              <View style={{ gap: theme.spacing(1.5) }}>
                <Caption color={theme.colors.textSecondary}>
                  {splitParticipantCount} {splitParticipantCount === 1 ? 'person' : 'people'} in this split
                </Caption>

                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Caption color={theme.colors.textSecondary}>Split Type</Caption>
                  <Body style={{ textTransform: 'capitalize' }}>
                    {expense.split.splitType === 'equal' ? 'Equal Split' : 'Custom Split'}
                  </Body>
                </View>

                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Caption color={theme.colors.textSecondary}>Paid By</Caption>
                  <Body>{getMemberName(expense.split.paidBy)}</Body>
                </View>

                <View style={{ marginTop: theme.spacing(1), paddingTop: theme.spacing(1.5), borderTopWidth: 1, borderTopColor: theme.colors.border }}>
                  <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1) }}>
                    Split Among
                  </Caption>
                  <View style={{ gap: theme.spacing(1) }}>
                    {sortedSplitEntries.map(([userId, amount]) => {
                      const isPayer = expense.split?.paidBy === userId;
                      const isCurrentUser = userId === currentUserId;
                      const pm = expense.paymentMethod;
                      const payerBadgeLabel =
                        isPayer && pm === 'cash'
                          ? 'Paid · Cash'
                          : isPayer && (pm === 'card' || pm === 'debit')
                            ? 'Paid · Card'
                            : isPayer
                              ? 'Paid'
                              : null;
                      return (
                        <View
                          key={userId}
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: theme.spacing(1),
                            borderRadius: theme.radii.md,
                            backgroundColor: isCurrentUser ? theme.colors.primary + '10' : theme.colors.backgroundSecondary,
                          }}
                        >
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1, flexWrap: 'wrap' }}>
                            <Body>{getMemberName(userId)}</Body>
                            {payerBadgeLabel && (
                              <View style={{
                                paddingHorizontal: theme.spacing(0.75),
                                paddingVertical: theme.spacing(0.25),
                                borderRadius: theme.radii.sm,
                                backgroundColor: theme.colors.primary + '20',
                              }}>
                                <Caption color={theme.colors.primary} style={{ fontSize: 10 }}>
                                  {payerBadgeLabel}
                                </Caption>
                              </View>
                            )}
                          </View>
                          <Body variant="bodyStrong">
                            {formatCurrency(amount, expense.currency || 'USD')}
                          </Body>
                        </View>
                      );
                    })}
                  </View>
                </View>

                {currentUserId && (
                  <View style={{ marginTop: theme.spacing(1.5), paddingTop: theme.spacing(1.5), borderTopWidth: 1, borderTopColor: theme.colors.border }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                      <Caption color={theme.colors.textSecondary}>Amount you paid</Caption>
                      <Body variant="bodyStrong" color={theme.colors.primary}>
                        {formatCurrency(getUserPaid(expense, currentUserId), expense.currency || 'USD')}
                      </Body>
                    </View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: theme.spacing(0.5) }}>
                      <Caption color={theme.colors.textSecondary}>Your share of the bill</Caption>
                      <Body variant="bodyStrong" color={theme.colors.error || '#FF3B30'}>
                        {formatCurrency(getUserShare(expense, currentUserId), expense.currency || 'USD')}
                      </Body>
                    </View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: theme.spacing(0.5) }}>
                      <Caption color={theme.colors.textSecondary}>
                        {getUserPaid(expense, currentUserId) - getUserShare(expense, currentUserId) >= 0
                          ? 'Your net (others may owe you)'
                          : 'Your net (you may owe others)'}
                      </Caption>
                      <Body variant="bodyStrong" color={
                        getUserPaid(expense, currentUserId) - getUserShare(expense, currentUserId) >= 0
                          ? theme.colors.success || '#34C759'
                          : theme.colors.error || '#FF3B30'
                      }>
                        {formatCurrency(
                          getUserPaid(expense, currentUserId) - getUserShare(expense, currentUserId),
                          expense.currency || 'USD'
                        )}
                      </Body>
                    </View>
                  </View>
                )}
              </View>
            </Card>
          )}

          {/* Actions */}
          {canEdit && (onEdit || onDelete) && (
            <View style={{ gap: theme.spacing(1.5) }}>
              {onEdit && (
                <Button
                  label="Edit Expense"
                  onPress={() => {
                    onClose();
                    onEdit(expense);
                  }}
                  icon="pencil-outline"
                />
              )}
              {onDelete && (
                <Button
                  label="Delete Expense"
                  variant="secondary"
                  onPress={() => {
                    onClose();
                    onDelete(expense);
                  }}
                  icon="trash-outline"
                />
              )}
            </View>
          )}
        </ScrollView>
      </View>

      {showNativePdfReceipt && nativeReceiptPdfUri ? (
        <DocumentAttachmentPdfModal
          visible
          uri={nativeReceiptPdfUri.startsWith('file') ? nativeReceiptPdfUri : `file://${nativeReceiptPdfUri}`}
          title="Receipt"
          onClose={closeReceiptViewer}
          onOpenElsewhere={async () => {
            try {
              if (await Sharing.isAvailableAsync()) {
                await Sharing.shareAsync(nativeReceiptPdfUri, {
                  mimeType: 'application/pdf',
                  UTI: 'com.adobe.pdf',
                  dialogTitle: 'Open receipt',
                });
              } else if (displayReceiptUrl) {
                await Linking.openURL(displayReceiptUrl);
              }
            } catch {
              Alert.alert('Receipt', 'Could not open this file in another app.');
            }
          }}
        />
      ) : null}

      {showNativePdfReceipt && !nativeReceiptPdfUri && !nativeReceiptPdfError ? (
        <Modal visible animationType="fade" transparent onRequestClose={closeReceiptViewer}>
          <View
            style={{
              flex: 1,
              backgroundColor: 'rgba(0,0,0,0.5)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: theme.spacing(2),
            }}
          >
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <ThemedText variant="body" color={theme.colors.text}>
              Loading receipt…
            </ThemedText>
            <Pressable onPress={closeReceiptViewer}>
              <ThemedText variant="body" color={theme.colors.primary}>
                Cancel
              </ThemedText>
            </Pressable>
          </View>
        </Modal>
      ) : null}

      {showNativePdfReceipt && nativeReceiptPdfError ? (
        <Modal visible animationType="fade" transparent onRequestClose={closeReceiptViewer}>
          <View
            style={{
              flex: 1,
              backgroundColor: 'rgba(0,0,0,0.5)',
              justifyContent: 'center',
              padding: theme.spacing(3),
            }}
          >
            <View
              style={{
                backgroundColor: theme.colors.surface,
                borderRadius: theme.radii.lg,
                padding: theme.spacing(2),
                gap: theme.spacing(2),
              }}
            >
              <ThemedText variant="bodyStrong">Could not load receipt</ThemedText>
              <ThemedText variant="body" color={theme.colors.textSecondary}>
                Try opening the original link in Safari or Files.
              </ThemedText>
              <Button
                label="Open link"
                variant="secondary"
                onPress={() => {
                  void Linking.openURL(displayReceiptUrl || receiptRemote);
                }}
              />
              <Button label="Close" variant="ghost" onPress={closeReceiptViewer} />
            </View>
          </View>
        </Modal>
      ) : null}

      {/* Receipt Viewer (images, web PDF, non-PDF) */}
      <Modal
        visible={Boolean(displayReceiptUrl) && showReceiptViewer && !showNativePdfReceipt}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={closeReceiptViewer}
      >
        <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: theme.spacing(2),
              paddingTop: modalTopInset + theme.spacing(2),
              paddingBottom: theme.spacing(2),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.border,
            }}
          >
            <ThemedText variant="heading">Receipt</ThemedText>
            <Pressable
              onPress={closeReceiptViewer}
              style={({ pressed }) => ({
                padding: theme.spacing(1),
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          </View>

          {displayReceiptUrl ? (
            (() => {
              const url = displayReceiptUrl;
              const isImage = /\.(png|jpe?g|webp|gif)(\?|$)/i.test(url);

              if (isImage) {
                return (
                  <View style={{ flex: 1, padding: theme.spacing(2) }}>
                    <Image
                      source={{ uri: url }}
                      style={{ flex: 1, width: '100%' }}
                      resizeMode="contain"
                      accessibilityLabel="Receipt image"
                    />
                  </View>
                );
              }

              return (
                <View style={{ flex: 1 }}>
                  <WebView
                    source={{ uri: url }}
                    style={{ flex: 1 }}
                    originWhitelist={getDocumentWebViewOriginWhitelist()}
                    onShouldStartLoadWithRequest={(request) =>
                      isDocumentPreviewWebViewUrl(request.url)
                    }
                    startInLoadingState
                    renderLoading={() => (
                      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <ActivityIndicator />
                      </View>
                    )}
                  />
                  <View style={{ padding: theme.spacing(2), borderTopWidth: 1, borderTopColor: theme.colors.borderMuted }}>
                    <Button
                      label="Open in another app"
                      variant="secondary"
                      onPress={async () => {
                        try {
                          const supported = await Linking.canOpenURL(url);
                          if (supported) {
                            await Linking.openURL(url);
                          }
                        } catch {
                          // ignore
                        }
                      }}
                      icon={<Ionicons name="share-outline" size={18} color={theme.colors.text} />}
                    />
                  </View>
                </View>
              );
            })()
          ) : null}
        </View>
      </Modal>
    </Modal>
  );
}

